import { useCallback } from 'react';

import { useListUserCauseSelections, useUpdateMyCausesSplits } from '@givebacks/api/core';
import {
  Affix,
  ButtonMantine,
  FullWidthButtonGroup,
  Modal,
  ModalProps,
  ScrollArea,
  Stack,
  notifications,
} from 'ui';

import { useFormListValidation } from '../../../../hooks';
import { getErrorMessage_v2 } from '../../../../utils';
import { ShopToGiveAllocations, UseAllocationForm } from '../../../Forms';

interface EditCashbackAllocationsModalProps extends ModalProps {
  title: React.ReactNode;
}

const EditCashbackAllocationsModalFooter = ({ onClose }: { onClose: () => void }) => {
  const { useGetForm, isFormsValid, useIsFormsDirty, resetForms } = useFormListValidation<{
    shopToGiveAllocationSplits: UseAllocationForm;
  }>();
  const form = useGetForm('shopToGiveAllocationSplits');
  const isDirty = useIsFormsDirty();

  const { mutate: updateCauseSplits } = useUpdateMyCausesSplits();

  const handleSave = useCallback(() => {
    const splits = Object.keys(form?.values?.allocations || {}).map((key) => ({
      cause_id: key,
      split: Number(form?.values?.allocations[key]),
    }));

    updateCauseSplits(splits, {
      onSuccess: () => {
        notifications.show({ variant: 'success', message: 'Updated Cashback Allocation' });
        onClose();
      },
      onError: (error) => {
        notifications.show({ variant: 'error', message: getErrorMessage_v2(error) });
      },
    });
  }, [onClose, form?.values, updateCauseSplits]);

  return (
    <Affix
      w="100%"
      py="lg"
      withinPortal={false}
      bg={'linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #FFFFFF 35.23%)'}
      pos="sticky"
    >
      <FullWidthButtonGroup>
        <ButtonMantine variant="subtle" disabled={!isDirty} onClick={() => resetForms()}>
          Reset Changes
        </ButtonMantine>
        <ButtonMantine disabled={!isFormsValid} onClick={() => handleSave()}>
          Save Changes
        </ButtonMantine>
      </FullWidthButtonGroup>
    </Affix>
  );
};

const EditCashbackAllocationsModalContent = ({ onClose }: { onClose: () => void }) => {
  const { data: causeSelections } = useListUserCauseSelections();
  return (
    <Stack gap="xs" px="lg">
      {causeSelections && <ShopToGiveAllocations causeSelections={causeSelections} />}
      <EditCashbackAllocationsModalFooter onClose={onClose} />
    </Stack>
  );
};

const EditCashbackAllocationsModal = ({
  opened,
  onClose,
  title,
}: EditCashbackAllocationsModalProps) => {
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={title}
      padding="lg"
      scrollAreaComponent={ScrollArea.Autosize}
      styles={{ body: { padding: 0 } }}
    >
      <EditCashbackAllocationsModalContent onClose={onClose} />
    </Modal>
  );
};

export { EditCashbackAllocationsModal };
